import type { ComponentType } from 'react';
// Round 7: per-icon subpath imports (not the '@phosphor-icons/react' barrel), so the
// static search panel island only bundles the icons it actually uses instead of the
// whole ~1300-icon package (the barrel's index.es.js bundles every icon's path data as
// one non-tree-shakeable object, confirmed by its own file size vs. one icon file's).
import { Gift } from '@phosphor-icons/react/dist/csr/Gift';
import { Clock } from '@phosphor-icons/react/dist/csr/Clock';
import { HeartStraight } from '@phosphor-icons/react/dist/csr/HeartStraight';
import { Stethoscope } from '@phosphor-icons/react/dist/csr/Stethoscope';
import { ForkKnife } from '@phosphor-icons/react/dist/csr/ForkKnife';
import { ChartLineUp } from '@phosphor-icons/react/dist/csr/ChartLineUp';
import { Basketball } from '@phosphor-icons/react/dist/csr/Basketball';
import { Money } from '@phosphor-icons/react/dist/csr/Money';
import { Books } from '@phosphor-icons/react/dist/csr/Books';
import { PiggyBank } from '@phosphor-icons/react/dist/csr/PiggyBank';
import { ShieldCheck } from '@phosphor-icons/react/dist/csr/ShieldCheck';
import { Baby } from '@phosphor-icons/react/dist/csr/Baby';
import { Bicycle } from '@phosphor-icons/react/dist/csr/Bicycle';
import { FirstAidKit } from '@phosphor-icons/react/dist/csr/FirstAidKit';
import { House } from '@phosphor-icons/react/dist/csr/House';
import { HandHeart } from '@phosphor-icons/react/dist/csr/HandHeart';
import { WifiHigh } from '@phosphor-icons/react/dist/csr/WifiHigh';
import { DeviceMobile } from '@phosphor-icons/react/dist/csr/DeviceMobile';
import { Bus } from '@phosphor-icons/react/dist/csr/Bus';
import { Umbrella } from '@phosphor-icons/react/dist/csr/Umbrella';

/**
 * Extracted from components/filters/FilterBuildingBlocks.tsx (step 5, static search panel
 * island): that file also pulls in redux hooks, LocationHierarchyPicker, FinlandGlobeFlagThumb,
 * and 20+ other phosphor icons used by its other exports (CustomRefinementList and friends), none
 * of which utils/search-panel/facet-icon.tsx's `getBenefitIcon` call actually needs. Importing
 * `getBenefitIcon` from there dragged that whole module graph into any bundle that only wanted
 * this one lookup, which is harmless inside the main app (already one bundle) but blew the size
 * budget for the standalone static search panel island (see
 * web-static/vite.island.config.ts and the step 5 report: ~400KB+ of unrelated code for a single
 * icon map). FilterBuildingBlocks.tsx re-exports `getBenefitIcon` from here so its own existing
 * import path (and EditorialDossier.tsx's) keeps working unchanged.
 *
 * Icon per benefit value, for CustomRefinementList's card variant (used by the Benefits section).
 * Benefit values are free text an LLM extracts from job postings, not a fixed enum, so this only
 * covers the common, well-formed ones actually seen in the data (checked against a live facet
 * count pull, not guessed); anything else falls back to Gift. Matched case-insensitively against
 * the raw facet value.
 */
const BENEFIT_ICONS: Record<string, ComponentType<{ className?: string; weight?: string }>> = {
  'flexible working hours': Clock,
  'well-being / healthcare wallet': HeartStraight,
  'occupational healthcare': Stethoscope,
  'lunch benefit': ForkKnife,
  'stock options / rsu': ChartLineUp,
  'sports & culture benefit': Basketball,
  'cash bonus': Money,
  'training budget': Books,
  'extra pension': PiggyBank,
  'accident / life insurance': ShieldCheck,
  'child-care voucher': Baby,
  'company bike': Bicycle,
  'extra health insurance': FirstAidKit,
  'remote-work allowance': House,
  'massage benefit': HandHeart,
  'home-internet benefit': WifiHigh,
  'work phone': DeviceMobile,
  'commuter pass': Bus,
  'holiday bonus': Umbrella,
  // Finnish and Swedish values of the same benefits: the fi/sv indexes store their own wording
  // (checked against the live benefits facet on 2026-09-30), so without these every fi/sv chip
  // fell back to the gift. Meal vouchers and extra holiday days share their English kin's icon.
  'joustavat työajat': Clock,
  'flexibla arbetstider': Clock,
  'hyvinvointietu': HeartStraight,
  'hälsoplånbok': HeartStraight,
  'wellbeing-plånbok': HeartStraight,
  'työterveyshuolto': Stethoscope,
  'laajennettu työterveyshuolto': Stethoscope,
  'företagshälsovård': Stethoscope,
  'utökad företagshälsovård': Stethoscope,
  'lounasetu': ForkKnife,
  'ateriaseteli': ForkKnife,
  'lunchförmån': ForkKnife,
  'måltidskuponger': ForkKnife,
  'osakeoptiot / rsu': ChartLineUp,
  'aktieoptioner / rsu': ChartLineUp,
  'liikunta- ja kulttuurietu': Basketball,
  'motion- och kulturförmån': Basketball,
  'rahabonus': Money,
  'kontantbonus': Money,
  'koulutusbudjetti': Books,
  'utbildningsbudget': Books,
  'lisäeläke': PiggyBank,
  'tilläggspension': PiggyBank,
  'tapaturma- / henkivakuutus': ShieldCheck,
  'olycksfalls- / livförsäkring': ShieldCheck,
  'lastenhoitoetu': Baby,
  'barnomsorgsförmån': Baby,
  'työsuhdepyörä': Bicycle,
  'företagscykel': Bicycle,
  'lisäterveysvakuutus': FirstAidKit,
  'tilläggssjukförsäkring': FirstAidKit,
  'etätyökorvaus': House,
  'distansarbetsersättning': House,
  'hierontaetu': HandHeart,
  'massageförmån': HandHeart,
  'kotinetu': WifiHigh,
  'heminternet': WifiHigh,
  'työpuhelin': DeviceMobile,
  'tjänstetelefon': DeviceMobile,
  'työsuhdematkalippu': Bus,
  'arbetsresebiljett': Bus,
  'lomaraha': Umbrella,
  'ylimääräiset lomapäivät': Umbrella,
  'semesterpenning': Umbrella,
  'extra semesterdagar': Umbrella,
};

/** Exported so the editorial-rail job-page redesign (TEC-512) and the search panel's facet icon
 * lookup can both reuse the same map instead of duplicating it. */
export const getBenefitIcon = (value: string): ComponentType<{ className?: string; weight?: string }> =>
  BENEFIT_ICONS[value.toLowerCase()] ?? Gift;
